/**
 * TableAdminConfirmQueue · the desk's on-switch.
 *
 * Founder direction (8 Feb 2026 evening):
 *   "The confirm-queue UI is the priority — it's the screen that
 *    actually turns rows green on the site. Nothing is live for
 *    members until the desk can work that queue, so this is the
 *    on-switch."
 *
 *   "YES to the 'Top 5 to confirm right now' strip — build it into
 *    the confirm-queue UI. That's exactly how I want the desk
 *    working: highest-impact rows first, cleared in under an hour
 *    each morning."
 *
 * Doctrine spine (still unchanged):
 *   • Nothing renders green to a member until human_confirmed=True.
 *   • Confirm is a positive action by a named concierge.
 *   • Reject is a positive action by a named concierge (row won't
 *     re-queue from a future ingest).
 *
 * Same /table paper-on-paper surface so it never feels like a leak
 * into another product. Same `useConsoleAuth` gate as /table/admin.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsoleAuth } from "@/lib/console-auth";
import {
    fetchArchiveQueue,
    confirmArchiveOverlay,
    rejectArchiveOverlay,
    getVenueDetail,
} from "@/lib/table-api";
import { BACKEND_URL } from "@/lib/backend-url";


// Read-only cockpit snapshot · single endpoint, no pagination.
// Used to surface the FULL queue scale at the top of the page even
// though the queue list itself paginates at 500 rows for perf.
async function fetchCockpitSnapshot() {
    const API = BACKEND_URL;
    const res = await fetch(`${API}/api/table/admin/cockpit-snapshot`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}


const PAGE_BG = "var(--t-paper)";
const CATEGORIES = [
    { key: null, label: "all" },
    { key: "michelin", label: "michelin" },
    { key: "fine_dine", label: "fine dine" },
    { key: "epicure", label: "epicure" },
    { key: "bar", label: "bar" },
    { key: "nightlife", label: "nightlife" },
];
const STATUSES = [
    { key: "staged", label: "staged" },
    { key: "confirmed", label: "confirmed" },
    { key: "rejected", label: "rejected" },
];


export default function TableAdminConfirmQueue() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useConsoleAuth();

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate(
                "/mira/console/login?return=/table/admin/confirm-queue",
                { replace: true },
            );
        }
    }, [user, authLoading, navigate]);

    if (authLoading || !user) {
        return (
            <main
                data-table-surface
                style={{
                    minHeight: "100vh", background: PAGE_BG,
                    color: "var(--t-ink2)", display: "flex",
                    alignItems: "center", justifyContent: "center",
                }}
            >
                <span
                    className="t-mono uppercase"
                    style={{ fontSize: 11, letterSpacing: "0.22em" }}
                >
                    · checking access
                </span>
            </main>
        );
    }

    return <Dashboard user={user} />;
}


function Dashboard({ user }) {
    const conciergeName =
        user?.name || user?.email || "Concierge\u00ae desk";
    const [status, setStatus] = useState("staged");
    const [category, setCategory] = useState(null);
    const [readBucket, setReadBucket] = useState(null);
    const [rows, setRows] = useState([]);
    const [fastTrackRows, setFastTrackRows] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            // Fast-track rows (both authoring paths: concierge_-
            // written_verified AND concierge_reviewed) are fetched
            // SEPARATELY so the full ~65+ surface even when scattered
            // through the 3,000+ staged rows beyond the paged limit.
            // Only meaningful when status=staged (no fast-track lane
            // on confirmed/rejected views).
            const fastTrackPromise = status === "staged"
                ? fetchArchiveQueue({
                    status: "staged",
                    category,
                    whisperOrigin: "concierge_written_verified,concierge_reviewed",
                    limit: 500,
                }).catch(() => ({ rows: [] }))
                : Promise.resolve({ rows: [] });
            const [res, ftRes, snap] = await Promise.all([
                fetchArchiveQueue({
                    status, category, readBucket, limit: 500,
                }),
                fastTrackPromise,
                fetchCockpitSnapshot().catch(() => null),
            ]);
            setRows(res.rows || []);
            setFastTrackRows((ftRes && ftRes.rows) || []);
            if (snap) setSnapshot(snap);
        } catch (e) {
            setErr(String(e?.message || e));
        } finally {
            setLoading(false);
        }
    }, [status, category, readBucket]);

    useEffect(() => { load(); }, [load]);

    // ─── Top-5 stack-rank (highest-impact rows first) ───────────────
    // Ranking (founder direction 8 Feb 2026): highest-impact rows
    // first. Practical signals available today:
    //   • match_status         · verified_2026 > confident > review
    //   • category_render_type · michelin > fine_dine > epicure > bar > nightlife
    //   • needs_currency_review · boost flagged rows so they get re-checked
    //   • created_at           · older first within a tier
    // Member-search-volume signal is parked for v2 (no per-venue
    // counter yet) · category_render_type is the proxy for "impact"
    // until we wire that.
    const top5 = useMemo(() => {
        if (status !== "staged") return [];
        const STATUS_RANK = {
            verified_2026: 4, confident: 3, review: 1,
        };
        const CATEGORY_RANK = {
            michelin: 5, fine_dine: 4, epicure: 3,
            bar: 2, nightlife: 1,
        };
        const score = (r) => {
            const statusS = STATUS_RANK[r.match_status] || 0;
            const catS = CATEGORY_RANK[r.category_render_type] || 0;
            const curr = r.needs_currency_review ? 2 : 0;
            // Older first within tier (older = more days = more bonus)
            const ageBonus = (
                Date.now() - new Date(r.created_at || 0).getTime()
            ) / 1000 / 86400; // days
            return catS * 1000 + statusS * 100 + curr * 20
                 + Math.min(ageBonus, 30);
        };
        return [...rows]
            .filter((r) => !r.rejected)
            .sort((a, b) => score(b) - score(a))
            .slice(0, 5);
    }, [rows, status]);

    // ─── Fast-track group (Aditya destination batches · 6 Feb 2026) ──
    // Rows authored AND verified by a human concierge (St. Moritz,
    // St. Tropez, 50 Best Bars, DJ Mag etc.). These don't need
    // research — the desk eyeballs + clicks. Surface them as their
    // own group ABOVE Top-5 so the desk knocks out the quick wins
    // first. Sourced from a SEPARATE fetch (`whisperOrigin` filter)
    // so all 18+ rows surface even when the main paged set tops out
    // at 500 of 3,800+ staged. Sort by destination cluster then category.
    const fastTrack = useMemo(() => {
        if (status !== "staged") return [];
        return [...fastTrackRows]
            .filter((r) => !r.rejected)
            .sort((a, b) => {
                const da = (a.destination || "").localeCompare(b.destination || "");
                if (da !== 0) return da;
                const CAT_RANK = { michelin: 5, fine_dine: 4, epicure: 3, bar: 2, nightlife: 1 };
                return (CAT_RANK[b.category_render_type] || 0)
                     - (CAT_RANK[a.category_render_type] || 0);
            });
    }, [fastTrackRows, status]);

    const totalCounts = useMemo(() => {
        const byCat = {};
        for (const r of rows) {
            byCat[r.category_render_type] =
                (byCat[r.category_render_type] || 0) + 1;
        }
        return { total: rows.length, byCat };
    }, [rows]);

    return (
        <main
            data-testid="table-admin-confirm-queue"
            data-table-surface
            style={{
                minHeight: "100vh", background: PAGE_BG,
                color: "var(--t-ink)",
            }}
        >
            <Header userName={conciergeName} />

            <div
                className="mx-auto px-6 py-8"
                style={{ maxWidth: 1180 }}
            >
                <TitleRow
                    totalCounts={totalCounts}
                    snapshot={snapshot}
                    status={status}
                    category={category}
                    onRefresh={load}
                    loading={loading}
                />

                <FilterBar
                    status={status} setStatus={setStatus}
                    category={category} setCategory={setCategory}
                />

                {/* Desk read-queue buckets (founder direction 8 Feb
                   2026 · 4-bucket top-down read list). The doctrine:
                   confirmed_by must always be a real person who
                   READ the row · NEVER a bulk greenlight. The buckets
                   tell the desk WHICH rows to work, in what order. */}
                {status === "staged" ? (
                    <ReadBucketStrip
                        snapshot={snapshot}
                        active={readBucket}
                        setActive={setReadBucket}
                    />
                ) : null}

                <DownloadStateBar snapshot={snapshot} />

                <ConciergeWhisperExcelBar snapshot={snapshot} />

                {err ? (
                    <div
                        data-testid="confirm-queue-error"
                        className="t-mono"
                        style={{
                            color: "var(--t-bad, #b04a4a)",
                            fontSize: 12,
                            padding: "10px 14px",
                            border: "1px solid var(--t-bad, #b04a4a)",
                            borderRadius: 8,
                            marginBottom: 20,
                        }}
                    >
                        {err}
                    </div>
                ) : null}

                {/* Fast-track group (concierge_written_verified rows ·
                   Aditya destination batches 6 Feb 2026) · ABOVE Top-5
                   so the desk knocks out the quick wins first. */}
                {status === "staged" && fastTrack.length > 0 ? (
                    <FastTrackStrip
                        rows={fastTrack}
                        conciergeName={conciergeName}
                        onChanged={load}
                    />
                ) : null}

                {/* Top-5 stack-rank */}
                {status === "staged" && top5.length > 0 ? (
                    <Top5Strip
                        rows={top5}
                        conciergeName={conciergeName}
                        onChanged={load}
                    />
                ) : null}

                <QueueList
                    rows={rows}
                    status={status}
                    conciergeName={conciergeName}
                    onChanged={load}
                    loading={loading}
                />

                <FirewallLine />
            </div>
        </main>
    );
}


function Header({ userName }) {
    return (
        <header
            style={{
                borderBottom: "1px solid var(--t-line2)",
                padding: "22px 28px",
                background: "var(--t-paper)",
            }}
        >
            <div className="mx-auto" style={{ maxWidth: 1180 }}>
                <div
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.22em",
                        color: "var(--t-ink3)", marginBottom: 4,
                    }}
                    data-testid="confirm-queue-eyebrow"
                >
                    Concierge\u00ae cockpit · the on-switch
                </div>
                <h1
                    className="t-serif"
                    style={{
                        fontSize: 22, fontWeight: 600,
                        lineHeight: 1.2, color: "var(--t-ink)",
                    }}
                    data-testid="confirm-queue-title"
                >
                    Archive overlay · confirm queue
                </h1>
                <div
                    className="t-mono"
                    style={{
                        fontSize: 11, color: "var(--t-ink3)",
                        marginTop: 6,
                    }}
                >
                    signed in as <span style={{ color: "var(--t-ink2)" }}>{userName}</span>
                </div>
            </div>
        </header>
    );
}


function TitleRow({
    totalCounts, snapshot, status, category, onRefresh, loading,
}) {
    const catLine = category
        ? ` · ${category.replace("_", " ")}`
        : "";
    // True totals from the cockpit snapshot (single-source-of-truth ·
    // unaffected by the 500-row queue limit).
    let scaleHint = "";
    if (snapshot) {
        let pageTotal = snapshot.staged_total;
        if (status === "confirmed") pageTotal = snapshot.confirmed_total;
        else if (status === "rejected") pageTotal = snapshot.rejected_total;
        if (category && snapshot.staged_by_category && status === "staged") {
            const c = snapshot.staged_by_category[category];
            if (typeof c === "number") pageTotal = c;
        }
        if (pageTotal > totalCounts.total) {
            scaleHint = ` · showing ${totalCounts.total} of ${pageTotal}`;
        } else if (pageTotal === totalCounts.total) {
            scaleHint = ` of ${pageTotal} total`;
        }
    }
    return (
        <div
            style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 18,
            }}
        >
            <div>
                <div
                    className="t-mono uppercase"
                    style={{
                        fontSize: 11, letterSpacing: "0.18em",
                        color: "var(--t-ink2)",
                    }}
                    data-testid="confirm-queue-status-line"
                >
                    {status}{catLine}
                </div>
                <div
                    className="t-mono"
                    style={{
                        fontSize: 13, color: "var(--t-ink2)",
                        marginTop: 4,
                    }}
                    data-testid="confirm-queue-total"
                >
                    {totalCounts.total} row{totalCounts.total === 1 ? "" : "s"}{scaleHint}
                </div>
                {snapshot && status === "staged" && !category ? (
                    <div
                        className="t-mono"
                        style={{
                            fontSize: 11, color: "var(--t-ink3)",
                            marginTop: 4,
                        }}
                        data-testid="confirm-queue-snapshot-line"
                    >
                        michelin {snapshot.staged_by_category?.michelin || 0}
                        {" · "}fine dine {snapshot.staged_by_category?.fine_dine || 0}
                        {" · "}bar {snapshot.staged_by_category?.bar || 0}
                        {" · "}nightlife {snapshot.staged_by_category?.nightlife || 0}
                        {" · "}epicure {snapshot.staged_by_category?.epicure || 0}
                        {snapshot.confirms_last_7d > 0 ? (
                            <>
                                {" \u2502 "}
                                <span style={{ color: "var(--t-accent, #2f7a3e)" }}>
                                    {snapshot.confirms_last_7d} confirmed in last 7d
                                </span>
                            </>
                        ) : null}
                    </div>
                ) : null}
            </div>
            <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                className="t-mono uppercase"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: loading ? "var(--t-ink3)" : "var(--t-ink2)",
                    background: "transparent",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 999,
                    padding: "6px 14px",
                    cursor: loading ? "default" : "pointer",
                }}
                data-testid="confirm-queue-refresh"
            >
                {loading ? "loading..." : "refresh"}
            </button>
        </div>
    );
}


function DownloadStateBar({ snapshot }) {
    const API = BACKEND_URL;
    const base = `${API}/api/table/admin/archive/export.csv`;
    const links = [
        { label: "all rows · everything in one file", href: base,
          count: "5,442 rows" },
        { label: "no_match · the ones that didn't cut it",
          href: `${base}?state=no_match`, count: "1,985 rows" },
        { label: "review_only · needs human eyes",
          href: `${base}?state=review_only`, count: "1,033 rows" },
        { label: "staged · in DB · ready for desk confirm",
          href: `${base}?state=staged`,
          count: snapshot ? `${snapshot.staged_total} rows` : "" },
        { label: "confirmed · rendering green",
          href: `${base}?state=confirmed`,
          count: snapshot ? `${snapshot.confirmed_total} rows` : "" },
    ];
    return (
        <div
            data-testid="confirm-queue-download-bar"
            style={{
                background: "var(--t-card)",
                border: "1px solid var(--t-line2)",
                borderRadius: 12,
                padding: "14px 16px 12px",
                marginBottom: 22,
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.22em",
                    color: "var(--t-ink3)", marginBottom: 10,
                }}
            >
                ⇩ download · what is what state
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {links.map((l) => (
                    <a
                        key={l.label}
                        href={l.href}
                        className="t-mono"
                        download
                        data-testid={`confirm-queue-download-${l.label.split(" ")[0].replace(/[^a-z0-9_]/gi, "")}`}
                        style={{
                            fontSize: 11,
                            color: "var(--t-ink2)",
                            background: "var(--t-paper)",
                            border: "1px solid var(--t-line2)",
                            borderRadius: 8,
                            padding: "7px 12px",
                            textDecoration: "none",
                            cursor: "pointer",
                        }}
                    >
                        <span style={{ color: "var(--t-ink)", fontWeight: 600 }}>
                            {l.label}
                        </span>
                        {l.count ? (
                            <span style={{ color: "var(--t-ink3)", marginLeft: 6 }}>
                                · {l.count}
                            </span>
                        ) : null}
                        <span style={{ marginLeft: 6, color: "var(--t-ink3)" }}>
                            ↓ csv
                        </span>
                    </a>
                ))}
            </div>
            <div
                className="t-mono"
                style={{
                    fontSize: 10, color: "var(--t-ink3)",
                    marginTop: 10, lineHeight: 1.5,
                }}
            >
                Each CSV shows db_state · match_status · resolved_name ·
                resolved_address · no_match_reason · name_drift ·
                confirmed_by · last_confirmed. Use the <em>no_match</em>
                file to feed a tighter re-run.
            </div>
        </div>
    );
}


function ConciergeWhisperExcelBar({ snapshot }) {
    // BC-027 · 10 Feb 2026 PM (founder direction: surface the live
    // Excel link on the desk console so the team can refresh the
    // workbook in one click without copying the URL each time).
    //
    // The endpoint is anti-cache · every download = fresh DB read.
    // The filename carries today's UTC date+hour stamp · two
    // downloads minutes apart land as distinct files in the desk
    // download folder.
    const API = BACKEND_URL;
    const links = [
        {
            label: "Michelin (★ · ★★ · ★★★ · BIB · Plate) · multi-tab Excel",
            href: `${API}/api/table/admin/archive/concierge-whispers.xlsx?scope=michelin`,
            hint: "one tab per city · ~150 sheets · pickup the one you need",
        },
        {
            label: "All whisper rows · multi-tab Excel",
            href: `${API}/api/table/admin/archive/concierge-whispers.xlsx`,
            hint: "every overlay row with a desk whisper · 910 tabs",
        },
        {
            label: "Pending Michelin venues · live CSV",
            href: `${API}/api/table/admin/archive/pending-michelin-venues.csv`,
            hint: "venues we have but Google place_id is missing · one-click Google Maps lookup per row",
        },
        {
            label: "All whispers · CSV (flat)",
            href: `${API}/api/table/admin/archive/concierge-whispers.csv`,
            hint: "single table · same 23 columns",
        },
        {
            label: "All whispers · JSON",
            href: `${API}/api/table/admin/archive/concierge-whispers.json`,
            hint: "for dashboards or scripts · ?city=Paris to scope",
        },
    ];
    return (
        <div
            data-testid="confirm-queue-whisper-export-bar"
            style={{
                background: "var(--t-card)",
                border: "1px solid var(--t-line2)",
                borderRadius: 12,
                padding: "14px 16px 12px",
                marginBottom: 22,
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.22em",
                    color: "var(--t-ink3)", marginBottom: 10,
                }}
            >
                ✦ live whisper export · refreshes on every click
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {links.map((l) => (
                    <a
                        key={l.label}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="t-mono"
                        data-testid={`whisper-export-${l.label.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                        style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            gap: 2,
                            fontSize: 11,
                            color: "var(--t-ink2)",
                            background: "var(--t-brass-wash)",
                            border: "1px solid var(--t-brass2)",
                            borderRadius: 8,
                            padding: "8px 12px",
                            textDecoration: "none",
                            cursor: "pointer",
                            maxWidth: 360,
                        }}
                    >
                        <span style={{
                            color: "var(--t-ink)", fontWeight: 600,
                            fontSize: 11,
                        }}>
                            {l.label}
                        </span>
                        <span style={{
                            color: "var(--t-ink3)", fontSize: 10,
                            fontWeight: 400,
                        }}>
                            {l.hint} ↓
                        </span>
                    </a>
                ))}
            </div>
            <div
                className="t-mono"
                style={{
                    fontSize: 10, color: "var(--t-ink3)",
                    marginTop: 10, lineHeight: 1.5,
                }}
            >
                Headers carry <em>Cache-Control: no-store</em> · every
                download is a fresh Mongo read. Filenames stamp the UTC
                date+hour so consecutive downloads land separately.
            </div>
        </div>
    );
}



function FilterBar({ status, setStatus, category, setCategory }) {
    return (
        <div style={{ marginBottom: 22 }}>
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.2em",
                    color: "var(--t-ink3)", marginBottom: 6,
                }}
            >
                status
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {STATUSES.map((s) => (
                    <button
                        key={s.key}
                        type="button"
                        onClick={() => setStatus(s.key)}
                        className="t-mono uppercase"
                        data-testid={`confirm-queue-filter-status-${s.key}`}
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            padding: "5px 12px",
                            borderRadius: 999,
                            border: "1px solid var(--t-line2)",
                            cursor: "pointer",
                            background: status === s.key
                                ? "var(--t-ink)" : "transparent",
                            color: status === s.key
                                ? "var(--t-paper)" : "var(--t-ink2)",
                        }}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.2em",
                    color: "var(--t-ink3)", marginBottom: 6,
                }}
            >
                category
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {CATEGORIES.map((c) => (
                    <button
                        key={c.key || "all"}
                        type="button"
                        onClick={() => setCategory(c.key)}
                        className="t-mono uppercase"
                        data-testid={`confirm-queue-filter-category-${c.key || "all"}`}
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            padding: "5px 12px",
                            borderRadius: 999,
                            border: "1px solid var(--t-line2)",
                            cursor: "pointer",
                            background: category === c.key
                                ? "var(--t-ink)" : "transparent",
                            color: category === c.key
                                ? "var(--t-paper)" : "var(--t-ink2)",
                        }}
                    >
                        {c.label}
                    </button>
                ))}
            </div>
        </div>
    );
}


function ReadBucketStrip({ snapshot, active, setActive }) {
    // Founder direction 8 Feb 2026 verbatim:
    //   "Use lc-DESK-GREENLIGHT-read-queue.csv — 4 buckets, work
    //    top-down: (1) verified-core fast confirms (authority-backed),
    //    (2) Michelin read, (3) named desk read, (4) unresolved —
    //    resolve before confirming. Desk reads each whisper, confirms
    //    what they read, confirmed_by = the actual person."
    //
    // This strip surfaces those buckets. The doctrine line under it
    // restates what the strip is and is NOT.
    const counts = (snapshot && snapshot.staged_by_read_bucket) || {};
    const BUCKETS = [
        {
            key: "1_VERIFIED_CORE_fast_confirm",
            label: "1 · verified-core · fast confirm",
            hint: "authority-backed early wins",
        },
        {
            key: "2_michelin_read",
            label: "2 · michelin read",
            hint: "starred rows · read whisper, then confirm",
        },
        {
            key: "3_named_desk_read",
            label: "3 · named desk read",
            hint: "named venue · read whisper, then confirm",
        },
        {
            key: "4_unresolved_resolve_first",
            label: "4 · unresolved · resolve first",
            hint: "no name yet · resolve before confirming",
        },
    ];
    const totalTagged = Object.values(counts).reduce(
        (a, b) => a + (b || 0), 0,
    );
    if (totalTagged === 0) return null;
    return (
        <div
            data-testid="confirm-queue-read-bucket-strip"
            style={{
                marginBottom: 22, paddingBottom: 16,
                borderBottom: "1px solid var(--t-line2)",
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.2em",
                    color: "var(--t-ink3)", marginBottom: 6,
                }}
            >
                desk read-queue · {totalTagged} rows tagged
            </div>
            <div style={{
                display: "flex", gap: 8, flexWrap: "wrap",
                marginBottom: 10,
            }}>
                <button
                    type="button"
                    onClick={() => setActive(null)}
                    className="t-mono uppercase"
                    data-testid="confirm-queue-read-bucket-all"
                    style={{
                        fontSize: 10, letterSpacing: "0.18em",
                        padding: "5px 12px", borderRadius: 999,
                        border: "1px solid var(--t-line2)",
                        cursor: "pointer",
                        background: active === null
                            ? "var(--t-ink)" : "transparent",
                        color: active === null
                            ? "var(--t-paper)" : "var(--t-ink2)",
                    }}
                >
                    all buckets
                </button>
                {BUCKETS.map((b) => {
                    const n = counts[b.key] || 0;
                    if (n === 0) return null;
                    const isActive = active === b.key;
                    return (
                        <button
                            key={b.key}
                            type="button"
                            onClick={() => setActive(b.key)}
                            className="t-mono uppercase"
                            data-testid={`confirm-queue-read-bucket-${b.key}`}
                            title={b.hint}
                            style={{
                                fontSize: 10, letterSpacing: "0.18em",
                                padding: "5px 12px", borderRadius: 999,
                                border: "1px solid var(--t-line2)",
                                cursor: "pointer",
                                background: isActive
                                    ? "var(--t-ink)" : "transparent",
                                color: isActive
                                    ? "var(--t-paper)" : "var(--t-ink2)",
                            }}
                        >
                            {b.label} · {n}
                        </button>
                    );
                })}
            </div>
            <p
                data-testid="confirm-queue-read-bucket-doctrine"
                className="t-mono"
                style={{
                    fontSize: 10, letterSpacing: "0.04em",
                    color: "var(--t-ink3)", lineHeight: 1.5,
                    margin: 0, fontStyle: "italic",
                }}
            >
                desk reads each whisper · confirms what they read ·
                confirmed_by = the actual person. the buckets tell the
                desk WHICH rows to work, in what order. nothing turns
                green until a Concierge® reads + confirms.
            </p>
        </div>
    );
}


function FastTrackStrip({ rows, conciergeName, onChanged }) {
    // Group rows by destination cluster (St. Moritz / St. Tropez / etc.)
    // so the desk works through a city at a time. Rows without a
    // destination land in a single "(no destination)" bucket — these
    // are the global authority lists (50 Best Bars / DJ Mag) that
    // span cities.
    const grouped = {};
    for (const r of rows) {
        const k = r.destination || "(authority lists)";
        if (!grouped[k]) grouped[k] = [];
        grouped[k].push(r);
    }
    const destinations = Object.keys(grouped).sort();
    return (
        <section
            data-testid="confirm-queue-fast-track"
            style={{
                background: "var(--t-card)",
                border: "1px solid var(--t-line2)",
                borderLeft: "3px solid var(--t-brass, #b08a3a)",
                borderRadius: 14,
                padding: "16px 18px 14px",
                marginBottom: 28,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 10,
                    marginBottom: 12,
                }}
            >
                <div
                    className="t-mono uppercase"
                    data-testid="confirm-queue-fast-track-eyebrow"
                    style={{
                        fontSize: 9, letterSpacing: "0.22em",
                        color: "var(--t-brass, #b08a3a)",
                        fontWeight: 700,
                    }}
                >
                    ★ fast-track · {rows.length} pre-verified · ~5 sec per confirm
                </div>
                <div
                    className="t-mono"
                    style={{
                        fontSize: 10, color: "var(--t-ink3)",
                    }}
                >
                    concierge_written_verified · eyeball + click
                </div>
            </div>
            {destinations.map((dest) => (
                <div key={dest} style={{ marginBottom: 16 }}>
                    <div
                        className="t-mono uppercase"
                        data-testid={`confirm-queue-fast-track-destination-${dest.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase()}`}
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            color: "var(--t-ink2)",
                            marginBottom: 10, fontWeight: 600,
                        }}
                    >
                        {dest} · {grouped[dest].length}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {grouped[dest].map((r) => (
                            <QueueRowCard
                                key={r.place_id}
                                row={r}
                                compact
                                conciergeName={conciergeName}
                                onChanged={onChanged}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </section>
    );
}


function Top5Strip({ rows, conciergeName, onChanged }) {
    return (
        <section
            data-testid="confirm-queue-top5"
            style={{
                background: "var(--t-card)",
                border: "1px solid var(--t-line2)",
                borderLeft: "3px solid var(--t-accent, #2f7a3e)",
                borderRadius: 14,
                padding: "16px 18px 14px",
                marginBottom: 28,
            }}
        >
            <div
                className="t-mono uppercase"
                data-testid="confirm-queue-top5-eyebrow"
                style={{
                    fontSize: 9, letterSpacing: "0.22em",
                    color: "var(--t-accent, #2f7a3e)",
                    marginBottom: 12, fontWeight: 700,
                }}
            >
                ✦ top 5 to confirm right now
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {rows.map((r, i) => (
                    <QueueRowCard
                        key={r.place_id}
                        row={r}
                        rank={i + 1}
                        compact
                        conciergeName={conciergeName}
                        onChanged={onChanged}
                    />
                ))}
            </div>
        </section>
    );
}


function QueueList({ rows, status, conciergeName, onChanged, loading }) {
    if (loading) {
        return (
            <div
                className="t-mono uppercase"
                data-testid="confirm-queue-loading"
                style={{
                    fontSize: 11, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                    padding: "40px 0",
                    textAlign: "center",
                }}
            >
                loading queue ·
            </div>
        );
    }
    if (rows.length === 0) {
        return (
            <div
                className="t-mono"
                data-testid="confirm-queue-empty"
                style={{
                    fontSize: 13, color: "var(--t-ink3)",
                    padding: "40px 0",
                    textAlign: "center",
                    fontStyle: "italic",
                }}
            >
                {status === "staged"
                    ? "Nothing waiting. The desk is clear."
                    : `No ${status} rows.`}
            </div>
        );
    }
    return (
        <div
            data-testid="confirm-queue-list"
            style={{
                display: "flex", flexDirection: "column", gap: 14,
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.22em",
                    color: "var(--t-ink3)", marginTop: 14,
                    marginBottom: 6,
                }}
            >
                all rows · {rows.length}
            </div>
            {rows.map((r) => (
                <QueueRowCard
                    key={r.place_id}
                    row={r}
                    conciergeName={conciergeName}
                    onChanged={onChanged}
                />
            ))}
        </div>
    );
}


function QueueRowCard({ row, rank, compact, conciergeName, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [resolved, setResolved] = useState(() => {
        // Prefer the row's own resolved_name/address (from the geocoder)
        // so we don't have to fetch /venue/{id} for every card. The
        // founder's lc_bulk_geocode.py output ships these directly on
        // the overlay record (no extra API call needed).
        if (row.resolved_name || row.resolved_address) {
            return {
                name: row.resolved_name,
                address: row.resolved_address,
                _from_record: true,
            };
        }
        return null;
    });
    const [error, setError] = useState(null);
    const [showWhisper, setShowWhisper] = useState(!compact);

    // Lazy-fetch the matched Google venue facts ONLY if the overlay
    // record doesn't already carry resolved_name/resolved_address.
    // Once the cleaning agent's full geocoded re-ingest lands, this
    // path won't run for any row · the queue page becomes one /queue
    // call with zero per-card network.
    useEffect(() => {
        if (resolved && resolved._from_record) return;
        let cancelled = false;
        (async () => {
            try {
                const v = await getVenueDetail(`gplaces:${row.place_id}`);
                if (!cancelled) setResolved(v);
            } catch {
                /* leave resolved null \u00b7 card still renders */
            }
        })();
        return () => { cancelled = true; };
    }, [row.place_id, resolved]);

    const isConfirmed = !!row.human_confirmed;
    const isRejected = !!row.rejected;
    const needsCurrency = !!row.needs_currency_review;
    const isSeasonal = !!row.seasonal;
    const isFastTrack = (
        row.whisper_origin === "concierge_written_verified"
        || row.whisper_origin === "concierge_reviewed"
    );

    const doConfirm = async () => {
        if (!window.confirm(
            `Confirm this row as ${row.original_archive_name}?\n\n`
            + `This flips the green gate · the whisper will start `
            + `rendering on the site immediately.`,
        )) return;
        setBusy(true);
        setError(null);
        try {
            await confirmArchiveOverlay(row.place_id, conciergeName);
            onChanged?.();
        } catch (e) {
            setError(String(e?.message || e));
        } finally {
            setBusy(false);
        }
    };

    const doReject = async () => {
        const reason = window.prompt(
            "Reason for rejection? (Optional · helps the audit trail)",
        );
        if (reason === null) return; // cancel
        if (!window.confirm(
            `Reject ${row.original_archive_name}?\n\n`
            + `The row will NOT render and won't re-queue.`,
        )) return;
        setBusy(true);
        setError(null);
        try {
            await rejectArchiveOverlay(row.place_id, conciergeName, reason);
            onChanged?.();
        } catch (e) {
            setError(String(e?.message || e));
        } finally {
            setBusy(false);
        }
    };

    const matchedName = resolved?.name;
    const matchedAddr = resolved?.address;
    const nameDrift = matchedName
        && matchedName.toLowerCase().trim()
            !== (row.original_archive_name || "").toLowerCase().trim();

    return (
        <div
            data-testid={`confirm-queue-card-${row.place_id}`}
            style={{
                background: "var(--t-paper)",
                border: "1px solid var(--t-line2)",
                borderRadius: 12,
                padding: "16px 18px 14px",
                opacity: isRejected ? 0.6 : 1,
            }}
        >
            {/* Top row: rank · name · category · flags */}
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    flexWrap: "wrap",
                    marginBottom: 10,
                }}
            >
                {rank ? (
                    <span
                        className="t-mono"
                        data-testid={`confirm-queue-card-rank`}
                        style={{
                            fontSize: 10,
                            color: "var(--t-accent, #2f7a3e)",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            background: "var(--t-paper2, #f4f1ea)",
                            border: "1px solid var(--t-line2)",
                            borderRadius: 999,
                            padding: "2px 8px",
                        }}
                    >
                        #{rank}
                    </span>
                ) : null}
                <span
                    className="t-serif"
                    data-testid="confirm-queue-card-name"
                    style={{
                        fontSize: 16, fontWeight: 600,
                        color: "var(--t-ink)",
                    }}
                >
                    {row.original_archive_name || "(unnamed)"}
                </span>
                <CategoryBadge cat={row.category_render_type} />
                <StatusPill status={row.match_status || "raw"} />
                {isFastTrack ? <FastTrackFlag /> : null}
                {isSeasonal ? <SeasonalFlag season={row.season} /> : null}
                {needsCurrency ? <CurrencyReviewFlag /> : null}
                {isConfirmed ? <ConfirmedStamp /> : null}
                {isRejected ? <RejectedStamp /> : null}
            </div>

            {/* Sub line · archive id · source · subcategory */}
            <div
                className="t-mono"
                style={{
                    fontSize: 11, color: "var(--t-ink3)",
                    marginBottom: 12,
                }}
                data-testid="confirm-queue-card-meta"
            >
                archive id <span style={{ color: "var(--t-ink2)" }}>{row.original_archive_id}</span>
                {row.source ? (
                    <>
                        {" · "}
                        source <span style={{ color: "var(--t-ink2)" }}>
                            {row.source}
                        </span>
                    </>
                ) : null}
                {row.subcategory ? (
                    <>
                        {" · "}
                        <span style={{ color: "var(--t-ink2)" }}>
                            {row.subcategory}
                        </span>
                    </>
                ) : null}
                {row.destination ? (
                    <>
                        {" · "}
                        <span style={{ color: "var(--t-brass, #b08a3a)" }}>
                            {row.destination}
                        </span>
                    </>
                ) : null}
            </div>

            {/* Known-for · sourced tag from destination batches.
               Surfaces above the matched-to-Google panel so the desk
               can sanity-check: does Google's resolved venue match
               the known-for the concierge recorded? */}
            {row.known_for ? (
                <div
                    data-testid="confirm-queue-card-known-for"
                    style={{
                        fontSize: 12.5, lineHeight: 1.45,
                        color: "var(--t-ink2)",
                        marginBottom: 12,
                        paddingLeft: 12,
                        borderLeft: "2px solid var(--t-brass, #b08a3a)",
                    }}
                >
                    <span
                        className="t-mono uppercase"
                        style={{
                            fontSize: 9, letterSpacing: "0.18em",
                            color: "var(--t-brass, #b08a3a)",
                            marginRight: 6, fontWeight: 700,
                        }}
                    >
                        known for
                    </span>
                    {row.known_for}
                </div>
            ) : null}

            {/* Resolved (Google) venue · the "right venue?" check */}
            <div
                style={{
                    background: "var(--t-card)",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 8,
                    padding: "10px 12px",
                    marginBottom: 12,
                }}
                data-testid="confirm-queue-card-resolved-venue"
            >
                <div
                    className="t-mono uppercase"
                    style={{
                        fontSize: 8.5, letterSpacing: "0.22em",
                        color: "var(--t-ink3)", marginBottom: 4,
                    }}
                >
                    matched to · per Google
                </div>
                {resolved === null ? (
                    <div
                        className="t-mono"
                        style={{ fontSize: 12, color: "var(--t-ink3)" }}
                    >
                        ... fetching
                    </div>
                ) : (
                    <>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 8,
                                flexWrap: "wrap",
                            }}
                        >
                            <span
                                className="t-serif"
                                style={{ fontSize: 14, color: "var(--t-ink)" }}
                            >
                                {matchedName || "(no venue found)"}
                            </span>
                            {nameDrift ? (
                                <span
                                    data-testid="confirm-queue-card-name-drift"
                                    className="t-mono uppercase"
                                    style={{
                                        fontSize: 8.5,
                                        letterSpacing: "0.18em",
                                        color: "var(--t-warn, #c47a2a)",
                                        background: "var(--t-paper2, #f4f1ea)",
                                        border: "1px solid var(--t-warn, #c47a2a)",
                                        borderRadius: 999,
                                        padding: "1px 7px",
                                    }}
                                >
                                    ✦ name drift
                                </span>
                            ) : null}
                        </div>
                        {matchedAddr ? (
                            <div
                                className="t-mono"
                                style={{
                                    fontSize: 11.5,
                                    color: "var(--t-ink2)",
                                    marginTop: 2,
                                }}
                            >
                                {matchedAddr}
                            </div>
                        ) : null}
                        <a
                            href={`https://www.google.com/maps/place/?q=place_id:${row.place_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="t-mono uppercase"
                            style={{
                                fontSize: 9, letterSpacing: "0.18em",
                                color: "var(--t-ink3)",
                                marginTop: 6,
                                display: "inline-block",
                                textDecoration: "underline",
                            }}
                            data-testid="confirm-queue-card-maps-link"
                        >
                            open in Google Maps ↗
                        </a>
                    </>
                )}
            </div>

            {/* Proposed whisper (the cleaned prose · or "show me") */}
            {row.concierge_whisper ? (
                <div style={{ marginBottom: 14 }}>
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 9, letterSpacing: "0.2em",
                            color: "var(--t-accent, #2f7a3e)",
                            marginBottom: 6, fontWeight: 700,
                        }}
                    >
                        proposed whisper
                    </div>
                    {showWhisper || !compact ? (
                        <p
                            data-testid="confirm-queue-card-whisper"
                            className="t-serif"
                            style={{
                                fontSize: 14, lineHeight: 1.5,
                                color: "var(--t-ink)",
                                fontStyle: "italic",
                                margin: 0,
                            }}
                        >
                            {row.concierge_whisper}
                        </p>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowWhisper(true)}
                            className="t-mono uppercase"
                            data-testid="confirm-queue-card-show-whisper"
                            style={{
                                fontSize: 9, letterSpacing: "0.18em",
                                color: "var(--t-ink2)",
                                background: "transparent",
                                border: "1px solid var(--t-line2)",
                                borderRadius: 999,
                                padding: "3px 10px",
                                cursor: "pointer",
                            }}
                        >
                            show whisper ↓
                        </button>
                    )}
                    {row.whisper_origin ? (
                        <div
                            className="t-mono uppercase"
                            style={{
                                fontSize: 8.5,
                                letterSpacing: "0.22em",
                                color: "var(--t-ink3)",
                                marginTop: 6,
                            }}
                        >
                            origin · {row.whisper_origin}
                        </div>
                    ) : null}
                </div>
            ) : (
                <div
                    className="t-mono"
                    style={{
                        fontSize: 12, color: "var(--t-ink3)",
                        marginBottom: 14, fontStyle: "italic",
                    }}
                    data-testid="confirm-queue-card-no-whisper"
                >
                    No whisper prose. Only category badge will render.
                </div>
            )}

            {error ? (
                <div
                    className="t-mono"
                    style={{
                        fontSize: 11, color: "var(--t-bad, #b04a4a)",
                        marginBottom: 10,
                    }}
                    data-testid="confirm-queue-card-error"
                >
                    {error}
                </div>
            ) : null}

            {/* Actions */}
            {!isConfirmed && !isRejected ? (
                <div
                    style={{
                        display: "flex", gap: 10, flexWrap: "wrap",
                    }}
                    data-testid="confirm-queue-card-actions"
                >
                    <button
                        type="button"
                        onClick={doConfirm}
                        disabled={busy}
                        className="t-mono uppercase"
                        data-testid="confirm-queue-card-confirm-btn"
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            padding: "8px 18px",
                            borderRadius: 999,
                            background: busy
                                ? "var(--t-ink3)" : "var(--t-accent, #2f7a3e)",
                            color: "var(--t-paper)",
                            border: "none",
                            cursor: busy ? "default" : "pointer",
                            fontWeight: 700,
                        }}
                    >
                        ✓ confirm
                    </button>
                    <button
                        type="button"
                        onClick={doReject}
                        disabled={busy}
                        className="t-mono uppercase"
                        data-testid="confirm-queue-card-reject-btn"
                        style={{
                            fontSize: 10, letterSpacing: "0.18em",
                            padding: "8px 18px",
                            borderRadius: 999,
                            background: "transparent",
                            color: "var(--t-ink2)",
                            border: "1px solid var(--t-line2)",
                            cursor: busy ? "default" : "pointer",
                        }}
                    >
                        ✕ reject
                    </button>
                </div>
            ) : null}
        </div>
    );
}


function CategoryBadge({ cat }) {
    if (!cat) return null;
    return (
        <span
            className="t-mono uppercase"
            data-testid={`confirm-queue-card-category-${cat}`}
            style={{
                fontSize: 9, letterSpacing: "0.18em",
                color: "var(--t-accent, #2f7a3e)",
                background: "var(--t-paper2, #f4f1ea)",
                border: "1px solid var(--t-line2)",
                borderRadius: 999,
                padding: "2px 9px",
                fontWeight: 700,
            }}
        >
            {cat.replace("_", " ")}
        </span>
    );
}


function StatusPill({ status }) {
    const colorMap = {
        verified_2026: "var(--t-accent, #2f7a3e)",
        confident: "var(--t-ink2)",
        review: "var(--t-warn, #c47a2a)",
        no_match: "var(--t-bad, #b04a4a)",
        raw: "var(--t-ink3)",
    };
    const c = colorMap[status] || "var(--t-ink3)";
    return (
        <span
            className="t-mono uppercase"
            data-testid={`confirm-queue-card-status-${status}`}
            style={{
                fontSize: 8.5, letterSpacing: "0.18em",
                color: c, border: `1px solid ${c}`,
                borderRadius: 999, padding: "1px 7px",
            }}
        >
            {status.replace("_", " ")}
        </span>
    );
}


function CurrencyReviewFlag() {
    return (
        <span
            className="t-mono uppercase"
            data-testid="confirm-queue-card-currency-review-flag"
            title="The currency of this fact needs re-checking (e.g., Michelin star may have moved)"
            style={{
                fontSize: 8.5, letterSpacing: "0.18em",
                color: "var(--t-warn, #c47a2a)",
                background: "var(--t-paper2, #f4f1ea)",
                border: "1px solid var(--t-warn, #c47a2a)",
                borderRadius: 999,
                padding: "1px 7px",
                fontWeight: 600,
            }}
        >
            ✱ currency review
        </span>
    );
}


function SeasonalFlag({ season }) {
    // `seasonal=TRUE` is treated the same as `needs_currency_review`
    // (founder direction 6 Feb 2026): the desk confirms "open this
    // season" at booking. Surface the season text inline if present.
    return (
        <span
            className="t-mono uppercase"
            data-testid="confirm-queue-card-seasonal-flag"
            title="Seasonal venue · desk confirms open-this-season at booking"
            style={{
                fontSize: 8.5, letterSpacing: "0.18em",
                color: "var(--t-warn, #c47a2a)",
                background: "var(--t-paper2, #f4f1ea)",
                border: "1px solid var(--t-warn, #c47a2a)",
                borderRadius: 999,
                padding: "1px 8px",
                fontWeight: 600,
            }}
        >
            ❄ seasonal{season ? ` · ${season}` : ""}
        </span>
    );
}


function FastTrackFlag() {
    return (
        <span
            className="t-mono uppercase"
            data-testid="confirm-queue-card-fast-track-flag"
            title="Concierge-written and web-verified before drop · eyeball-and-click confirm"
            style={{
                fontSize: 8.5, letterSpacing: "0.18em",
                color: "var(--t-brass, #b08a3a)",
                background: "var(--t-paper2, #f4f1ea)",
                border: "1px solid var(--t-brass, #b08a3a)",
                borderRadius: 999,
                padding: "1px 8px",
                fontWeight: 700,
            }}
        >
            ★ fast-track
        </span>
    );
}


function ConfirmedStamp() {
    return (
        <span
            className="t-mono uppercase"
            data-testid="confirm-queue-card-confirmed-stamp"
            style={{
                fontSize: 8.5, letterSpacing: "0.2em",
                color: "var(--t-accent, #2f7a3e)",
                border: "1px solid var(--t-accent, #2f7a3e)",
                borderRadius: 999, padding: "1px 8px",
                fontWeight: 700,
            }}
        >
            ✓ confirmed
        </span>
    );
}


function RejectedStamp() {
    return (
        <span
            className="t-mono uppercase"
            data-testid="confirm-queue-card-rejected-stamp"
            style={{
                fontSize: 8.5, letterSpacing: "0.2em",
                color: "var(--t-bad, #b04a4a)",
                border: "1px solid var(--t-bad, #b04a4a)",
                borderRadius: 999, padding: "1px 8px",
                fontWeight: 700,
            }}
        >
            ✕ rejected
        </span>
    );
}


function FirewallLine() {
    return (
        <p
            className="t-mono uppercase text-center"
            data-testid="confirm-queue-firewall-line"
            style={{
                fontSize: 9, letterSpacing: "0.22em",
                color: "var(--t-ink3)",
                marginTop: 60,
                fontStyle: "italic",
            }}
        >
            doctrine \u00b7 nothing renders green until a Concierge\u00ae
            confirms \u00b7 the green gate is sacred
        </p>
    );
}
